/**
 * CreateFranchiseUserForm Component
 *
 * This component renders a form to create a new franchise user. It allows input of user details such as name, email, phone,
 * role selection, terminal assignment, and address fields. The available roles are dynamically filtered based on the
 * logged-in user's role.
 *
 * Props:
 * @param {Function} onSubmit - Function to handle form submission with the new member data.
 * @param {Function} onClose - Function to close the form modal.
 * @param {Array} terminals - List of available terminals for assignment.
 * @param {number} loggedInUserRole - The role of the currently logged-in user.
 */
import React, { useState } from "react"
import { ClipLoader } from "react-spinners"
import { Logger } from "../../infrastructure/services/logger"
import { FranchiseRole } from "../../infrastructure/models/enums/franchiserole"
import { NewUser } from "../../infrastructure/models/user"
import { Terminal as TerminalModel } from "../../infrastructure/models/user"
import { container } from "../../infrastructure/container"
import { TYPES } from "../models/types"
import { ActionButtonVariant } from "../models/enum/ActionButtonVariant"
import ActionButton from "./button/ActionButton"

/**
 * Props for the CreateFranchiseUserForm component.
 *
 * This interface defines the properties required by the CreateFranchiseUserForm component,
 * including callbacks for handling form submission and closure, user role information,
 * terminal data, and user creation status.
 */
export interface CreateFranchiseUserFormProps {
  /**
   * Callback function triggered when a new user is submitted.
   *
   * @param {NewUser} newUser - The newly created franchise user.
   */
  onSubmit: (newUser: NewUser) => void

  /**
   * Callback function to close the form.
   */
  onClose: () => void

  /**
   * List of available terminals for user assignment.
   */
  terminals: TerminalModel[]

  /**
   * Role of the currently logged-in user.
   */
  loggedInUserRole: FranchiseRole

  /**
   * Indicates whether a user creation process is in progress.
   */
  isCreatingUser: boolean
}

const CreateFranchiseUserForm: React.FC<CreateFranchiseUserFormProps> = ({
  onSubmit,
  onClose,
  terminals,
  loggedInUserRole,
  isCreatingUser,
}) => {
  const logger = container.resolve<Logger>("logger")

  const [formData, setFormData] = useState<NewUser>({
    firstname: "",
    lastname: "",
    email: "",
    phone: {
      primary: "",
      secondary: "",
    },
    role: FranchiseRole.USER,
    terminals: [],
    address: {
      street: "",
      city: "",
      state: "",
      postalCode: "",
    },
  })

  /**
   * Handles input field changes and updates the state.
   * @param {React.ChangeEvent<HTMLInputElement | HTMLSelectElement>} e - The change event.
   */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    if (name === TYPES.ROLE) {
      // Convert role string to integer
      setFormData({
        ...formData,
        role: parseInt(value, 10),
      })
    } else {
      setFormData({ ...formData, [name]: value })
    }
  }

  /**
   * Filters and retrieves available roles based on the logged-in user's role.
   */
  const getFilteredRoles = () => {
    return Object.entries(FranchiseRole)
      .filter(([key, value]) => typeof value === TYPES.NUMBER)
      .filter(([key]) => {
        if (loggedInUserRole === FranchiseRole.OWNER) {
          return key !== "OWNER"
        } else if (loggedInUserRole === FranchiseRole.ADMIN) {
          return key !== "OWNER" && key !== "ADMIN"
        }
        return true
      })
  }

  /**
   * Handles selection of terminals by updating the state.
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event.
   */
  const handleTerminalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target
    if (checked) {
      setFormData({
        ...formData,
        terminals: [...formData.terminals, value],
      })
    } else {
      setFormData({
        ...formData,
        terminals: formData.terminals.filter((id) => id !== value),
      })
    }
  }

  /**
   * Handles form submission and calls onSubmit with the form data.
   * @param {React.FormEvent} e - The form submit event.
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      onSubmit(formData)
    } catch (error) {
      logger.error("Error creating franchise user:", error)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white p-4 rounded-lg w-1/4">
        <h2
          data-automation-id="createfranhiseuserform-form-text"
          id="createfranhiseuserform-form-text"
          className="text-xl font-semibold mb-2"
        >
          Create New Member
        </h2>
        <form
          data-automation-id="createfranchiseuserform-form"
          id="createfranchiseuserform-form"
          onSubmit={handleSubmit}
        >
          {/* Form fields */}
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label
                data-automation-id="createfranchiseuserform-firstname-label"
                id="createfranchiseuserform-firstname-label"
                className="block text-xs font-medium text-gray-700"
              >
                First Name
              </label>
              <input
                data-automation-id="createfranchiseuserform-inputbox-firstname-input"
                id="createfranchiseuserform-inputbox-firstname-input"
                type={TYPES.TEXT}
                name="firstname"
                value={formData.firstname}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-1"
                required
              />
            </div>
            <div>
              <label
                data-automation-id="createfranchiseuserform-lastname-label"
                id="createfranchiseuserform-lastname-label"
                className="block text-xs font-medium text-gray-700"
              >
                Last Name
              </label>
              <input
                data-automation-id="createfranchiseuserform-inputbox-lastname-input"
                id="createfranchiseuserform-inputbox-lastname-input"
                type={TYPES.TEXT}
                name="lastname"
                value={formData.lastname}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-1"
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label
                data-automation-id="createfranchiseuserform-phone-label"
                id="createfranchiseuserform-phone-label"
                className="block text-xs font-medium text-gray-700"
              >
                Phone
              </label>
              {/* Primary Phone (Required) */}
              <label
                htmlFor="primaryPhone"
                className="block text-xs font-medium text-red-600"
              >
                Primary(Required)*
              </label>
              <div className="mt-1 flex w-full rounded-md shadow-sm">
                <span className="inline-flex items-center px-2 rounded-l-md border border-r-0 border-gray-300 bg-gray-100 text-gray-600 text-sm">
                  +91
                </span>
                <input
                  id="primaryPhone"
                  type="text"
                  name="primaryPhone"
                  value={formData.phone.primary.replace("+91", "")}
                  onChange={(e) => {
                    const onlyDigits = e.target.value.replace(/\D/g, "")
                    setFormData({
                      ...formData,
                      phone: {
                        ...formData.phone,
                        primary: `+91${onlyDigits}`,
                      },
                    })
                  }}
                  className="flex-1 min-w-0 block w-full rounded-r-md border border-gray-300 p-1"
                  maxLength={10}
                  pattern="\d{10}"
                  placeholder="Primary*"
                  required
                />
              </div>

              {/* Secondary Phone (Optional) */}
              <label
                htmlFor="secondaryPhone"
                className="block text-xs font-medium text-gray-700"
              >
                Secondary(Optional)
              </label>
              <div className="mt-1 flex w-full rounded-md shadow-sm">
                <span className="inline-flex items-center px-2 rounded-l-md border border-r-0 border-gray-300 bg-gray-100 text-gray-600 text-sm">
                  +91
                </span>
                <input
                  id="secondaryPhone"
                  type="text"
                  name="secondaryPhone"
                  value={formData.phone.secondary?.replace("+91", "") || ""}
                  onChange={(e) => {
                    const onlyDigits = e.target.value.replace(/\D/g, "")
                    setFormData({
                      ...formData,
                      phone: {
                        ...formData.phone,
                        secondary: `+91${onlyDigits}`,
                      },
                    })
                  }}
                  className="flex-1 min-w-0 block w-full rounded-r-md border border-gray-300 p-1"
                  maxLength={10}
                  pattern="\d{10}"
                  placeholder="Optional"
                />
              </div>
            </div>
            <div>
              <label
                data-automation-id="createfranchiseuserform-email-label"
                id="createfranchiseuserform-email-label"
                className="block text-xs font-medium text-gray-700"
              >
                Email
              </label>
              <input
                data-automation-id="createfranchiseuserform-inputbox-email-input"
                id="createfranchiseuserform-inputbox-email-input"
                type={TYPES.EMAIL}
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-1"
                required
              />
            </div>
            <div>
              <label
                data-automation-id="createfranchiseuserform-role-label"
                id="createfranchiseuserform-role-label"
                className="block text-xs font-medium text-gray-700"
              >
                Role
              </label>
              <select
                data-automation-id="createfranchiseuserform-dropdown-role-select"
                id="createfranchiseuserform-dropdown-role-select"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-1"
                required
              >
                {getFilteredRoles().map(([key, value]) => (
                  <option key={key} value={value}>
                    {key.charAt(0) + key.slice(1).toLowerCase()}{" "}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mb-2">
            <label
              data-automation-id="createfranchiseuserform-terminal-label"
              id="createfranchiseuserform-terminal-label"
              className="block text-xs font-medium text-gray-700"
            >
              Terminal Assigned
            </label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {terminals.map((terminal) => (
                <div key={terminal.terminalId} className="flex items-center">
                  <input
                    type={TYPES.CHECKBOX}
                    id={terminal.terminalId}
                    value={terminal.terminalId}
                    checked={formData.terminals.includes(terminal.terminalId)}
                    onChange={handleTerminalChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <label
                    data-automation-id="createfranchiseuserform-terminalname-label"
                    id="createfranchiseuserform-terminalname-label"
                    htmlFor={terminal.terminalId}
                    className="ml-1 block text-xs text-gray-900"
                  >
                    {terminal.name}
                  </label>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label
                data-automation-id="createfranchiseuserform-address-street-label"
                id="createfranchiseuserform-address-street-label"
                className="block text-xs font-medium text-gray-700"
              >
                Street
              </label>
              <input
                data-automation-id="createfranchiseuserform-inputbox-address-street-input"
                id="createfranchiseuserform-inputbox-address-street-input"
                type={TYPES.TEXT}
                name="street"
                value={formData.address.street}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    address: { ...formData.address, street: e.target.value },
                  })
                }
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-1"
                required
              />
            </div>
            <div>
              <label
                data-automation-id="createfranchiseuserform-address-city-label"
                id="createfranchiseuserform-address-city-label"
                className="block text-xs font-medium text-gray-700"
              >
                City
              </label>
              <input
                data-automation-id="createfranchiseuserform-inputbox-address-city-input"
                id="createfranchiseuserform-inputbox-address-city-input"
                type={TYPES.TEXT}
                name="city"
                value={formData.address.city}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    address: { ...formData.address, city: e.target.value },
                  })
                }
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-1"
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label
                data-automation-id="createfranchiseuserform-address-state-label"
                id="createfranchiseuserform-address-state-label"
                className="block text-xs font-medium text-gray-700"
              >
                State
              </label>
              <input
                data-automation-id="createfranchiseuserform-inputbox-address-state-input"
                id="createfranchiseuserform-inputbox-address-state-input"
                type={TYPES.TEXT}
                name="state"
                value={formData.address.state}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    address: { ...formData.address, state: e.target.value },
                  })
                }
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-1"
                required
              />
            </div>
            <div>
              <label
                data-automation-id="createfranchiseuserform-address-postalcode-label"
                id="createfranchiseuserform-address-postalcode-label"
                className="block text-xs font-medium text-gray-700"
              >
                Postal Code
              </label>
              <input
                data-automation-id="createfranchiseuserform-inputbox-address-postalcode-input"
                id="createfranchiseuserform-inputbox-address-postalcode-input"
                type={TYPES.TEXT}
                name="postalCode"
                value={formData.address.postalCode}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    address: {
                      ...formData.address,
                      postalCode: e.target.value,
                    },
                  })
                }
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-1"
                required
              />
            </div>
          </div>
          {/* Submit and Cancel buttons */}
          <div className="flex justify-end space-x-2 w-full">
            <ActionButton
              variant={ActionButtonVariant.CANCEL}
              data-automation-id="createfranchiseuserform-close-form-button"
              id="createfranchiseuserform-close-form-button"
              onClick={onClose}
            />
            <ActionButton
              variant={ActionButtonVariant.SAVE}
              data-automation-id="createfranchiseuserform-save-user-details-button"
              id="createfranchiseuserform-save-user-details-button"
              onSubmit={handleSubmit}
              disabled={isCreatingUser}
            />
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateFranchiseUserForm